import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Users, Globe, Calendar, Mail, ArrowLeft, RefreshCw, Loader2,
  ChevronLeft, ChevronRight, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country code to flag emoji
const getCountryFlag = (countryCode) => {
  if (!countryCode) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Country code to name mapping (common ones)
const countryNames = {
  FI: 'Suomi',
  SE: 'Ruotsi',
  NO: 'Norja',
  DK: 'Tanska',
  DE: 'Saksa',
  US: 'Yhdysvallat',
  GB: 'Iso-Britannia',
  FR: 'Ranska',
  ES: 'Espanja',
  IT: 'Italia',
  NL: 'Alankomaat',
  PL: 'Puola',
  EE: 'Viro',
  LV: 'Latvia',
  LT: 'Liettua',
  RU: 'Venäjä',
  CA: 'Kanada',
  AU: 'Australia',
  JP: 'Japani',
  CN: 'Kiina',
  IN: 'Intia',
  BR: 'Brasilia'
};

const getCountryName = (code) => {
  if (!code) return 'Tuntematon';
  return countryNames[code] || code;
};

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 20;

  const loadUsers = useCallback(async (pageNum = 0) => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        params: { skip: pageNum * limit, limit },
        withCredentials: true
      });
      setData(response.data);
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load users:', error);
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(page);
  }, [loadUsers, page]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadUsers(page);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fi-FI', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Ladataan käyttäjiä...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Admin-oikeudet vaaditaan</h2>
            <p className="text-muted-foreground mb-6">Sinulla ei ole oikeuksia tarkastella tätä sivua.</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Takaisin
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-extrabold tracking-tight">
              Käyttäjät <span className="text-primary">({data?.total || 0})</span>
            </h1>
            <p className="text-muted-foreground mt-1">
              Kaikki rekisteröityneet käyttäjät
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              className="rounded-full"
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Päivitä
            </Button>
            <Button 
              onClick={() => navigate('/admin/analytics')} 
              variant="outline" 
              className="rounded-full"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Analytics
            </Button>
          </div>
        </div>

        {/* Country Stats */}
        {data?.country_stats?.length > 0 && (
          <div className="rounded-2xl bg-white border border-stone-100 p-6 shadow-sm mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Käyttäjät maittain</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {data.country_stats.map((stat) => (
                <div 
                  key={stat.country} 
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-stone-50 border border-stone-100"
                >
                  <span className="text-xl">{getCountryFlag(stat.country)}</span>
                  <span className="font-medium">{getCountryName(stat.country)}</span>
                  <span className="text-muted-foreground">({stat.count})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="rounded-2xl bg-white border border-stone-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-100">
                  <th className="text-left py-4 px-6 font-semibold text-sm">Käyttäjä</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm">Sähköposti</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm">Maa</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm">Kirjautumistapa</th>
                  <th className="text-left py-4 px-6 font-semibold text-sm">Rekisteröitynyt</th>
                </tr>
              </thead>
              <tbody>
                {data?.users?.map((user, index) => (
                  <tr 
                    key={user.user_id} 
                    className={`border-b border-stone-50 hover:bg-stone-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50/30'}`}
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img 
                            src={user.picture} 
                            alt={user.name} 
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <span className="font-medium">{user.name || '-'}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span className="text-sm">{user.email}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {user.country ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getCountryFlag(user.country)}</span>
                          <span className="text-sm">{getCountryName(user.country)}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.auth_method === 'google' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {user.auth_method === 'google' ? 'Google' : 'Magic Link'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">{formatDate(user.created_at)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-stone-100">
              <p className="text-sm text-muted-foreground">
                Näytetään {page * limit + 1}-{Math.min((page + 1) * limit, data?.total || 0)} / {data?.total || 0}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-full"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="rounded-full"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
